import { FC } from 'react'
import { EChartOption } from 'echarts'
import { EChart } from 'src/charts/EChart'
import styles from './multiTopSources.module.scss'
import { MultiCampaign, MultiSeries } from '../interfaces'
import { campaignColor } from '../palette'

interface MultiTopSourcesProps {
  campaigns: MultiCampaign[]
  series: MultiSeries[]
  sources: string[]
}

const COPY = {
  subtitle:
    'How mention share shifted across sources between the selected periods.',
  title: 'Top Sources — Share of Mentions',
}

const CHART_HEIGHT = 420
const CHART_WIDTH = 1130

// Grouped rather than stacked: these are shares of each campaign's own
// mentions, so they do not sum to anything and a stack would invent a total
// that means nothing.
export const MultiTopSources: FC<MultiTopSourcesProps> = ({
  campaigns,
  series,
  sources,
}) => {
  const options: EChartOption = {
    grid: { bottom: 72, containLabel: true, left: 8, right: 8, top: 16 },
    legend: {
      bottom: 0,
      itemGap: 24,
      itemHeight: 12,
      itemWidth: 12,
      textStyle: {
        color: '#25262d',
        fontFamily: 'Roboto, sans-serif',
        fontSize: 13,
      },
      type: 'plain',
    },
    series: campaigns.map((campaign, index) => ({
      barGap: '20%',
      data: series.find((row) => row.campaignId === campaign.id)?.values ?? [],
      itemStyle: { barBorderRadius: [4, 4, 0, 0], color: campaignColor(index) },
      name: campaign.name,
      type: 'bar',
    })),
    tooltip: {
      axisPointer: { type: 'shadow' },
      formatter: (params) => {
        const rows = Array.isArray(params) ? params : [params]

        return [
          rows[0]?.name ?? '',
          ...rows.map((row) => `${row.seriesName}: ${row.value}%`),
        ].join('<br/>')
      },
      trigger: 'axis',
    },
    xAxis: {
      axisLabel: {
        color: '#000000b2',
        fontFamily: 'Roboto, sans-serif',
        fontSize: 12,
        margin: 12,
      },
      axisLine: { lineStyle: { color: '#e6ebfa' } },
      axisTick: { show: false },
      data: sources,
      type: 'category',
    },
    yAxis: {
      axisLabel: {
        color: '#000000b2',
        fontFamily: 'Roboto, sans-serif',
        fontSize: 12,
        formatter: (value: number) => `${value}%`,
      },
      axisLine: { show: false },
      axisTick: { show: false },
      max: 100,
      min: 0,
      splitLine: { lineStyle: { color: '#e6ebfa', type: 'dashed' } },
      type: 'value',
    },
  }

  return (
    <section className={styles.card}>
      <div className={styles.header}>
        <h3 className={styles.title}>{COPY.title}</h3>
        <p className={styles.subtitle}>{COPY.subtitle}</p>
      </div>

      <EChart height={CHART_HEIGHT} options={options} width={CHART_WIDTH} />
    </section>
  )
}
